import { isLikelyLfsPointer } from "@core/GitLfs/logic/isLikelyLfsPointer";
import type { CatalogLogo } from "@core-ui/ContextServices/CatalogLogoService/catalogLogoHooks";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import SourceDataService from "@core-ui/ContextServices/SourceDataService";
import Workspace from "@core-ui/ContextServices/Workspace";
import { resolveFileKind } from "@core-ui/utils/resolveFileKind";
import { isLogoEmoji, isLogoIcon, resolveRawLogo } from "@ext/catalog/logo/catalogLogoIcon";
import {
	type AvailableCatalogsServer,
	gesCloudServer,
	gesServer,
	getClientServerUrl,
} from "@ext/enterprise/utils/availableCatalogsServer";
import { getEnterpriseSourceData } from "@ext/enterpriseCommon/logic/storage/getEnterpriseSourceData";
import type { CatalogLink } from "@ext/navigation/NavigationLinks";
import { useSetting } from "@ext/settings/logic/hooks";
import Theme from "@ext/Theme/Theme";
import useCachedLogo from "./useCachedLogo";

const isFileLogo = (logo: string | undefined): boolean => !!logo && !isLogoIcon(logo) && !isLogoEmoji(logo);

const fetchLogoFile = async (server: AvailableCatalogsServer, repo: string, variant: Theme): Promise<Blob | null> => {
	const blob = await server.logo(repo, variant);
	if (!blob) return null;

	const bytes = new Uint8Array(await blob.arrayBuffer());
	const buffer = Buffer.from(bytes);
	if (isLikelyLfsPointer(buffer)) return null;

	return new Blob([bytes], { type: resolveFileKind(buffer) });
};

const getServer = (): AvailableCatalogsServer | undefined => {
	const { conf, isLogged } = PageDataContextService.value;
	const target = getClientServerUrl(conf.enterpriseCloud, Workspace.current()?.enterprise?.gesUrl);
	if (!target) return;
	if (target.isCloud) return isLogged ? gesCloudServer(target.url) : undefined;

	const token = getEnterpriseSourceData(SourceDataService.value ?? [], target.url)?.token;
	return token ? gesServer(target.url, token) : undefined;
};

/** Fetched as a blob: a bare `<img src>` can't carry the `Authorization` header GES requires. */
const useLogoFile = (repo: string | undefined, logo: string | undefined, variant: Theme): CatalogLogo | null => {
	const server = getServer();
	const canFetch = isFileLogo(logo) && !!server && !!repo;
	const key = canFetch ? JSON.stringify(["available", server.url, repo, logo, variant]) : null;

	return useCachedLogo(key, () => fetchLogoFile(server, repo, variant));
};

const useAvailableCatalogLogo = (link?: CatalogLink): CatalogLogo | null => {
	const [theme] = useSetting("general.theme");
	const light = useLogoFile(link?.repo, link?.logo, Theme.light);
	const dark = useLogoFile(link?.repo, link?.logoDark, Theme.dark);

	const showDark = theme === Theme.dark && !!link?.logoDark;
	return resolveRawLogo(showDark ? link.logoDark : link?.logo) ?? (showDark ? dark : light);
};

export default useAvailableCatalogLogo;
